<template>
  <div class="trades-page">
    <header class="signals-header">
      <div class="filter-group">
        <button
          v-for="option in filterOptions"
          :key="option.value"
          class="filter-button"
          :class="{ active: activeFilter === option.value }"
          @click="setFilter(option.value)"
        >
          {{ option.label }}
        </button>
      </div>
    </header>

    <div v-if="isLoading" class="loading-banner">Carregando trades reais da Bybit...</div>
    <div v-if="errorMessage" class="error-banner">{{ errorMessage }}</div>

    <section class="signals-grid" @mouseleave="hoveredIndex = null">
      <article
        v-for="(trade, index) in filteredTrades"
        :key="index"
        :class="['signal-card', trade.state, { 'is-hovered': hoveredIndex === index }]"
        @mouseenter="handleCardMouseEnter(index)"
        @mouseleave="handleCardMouseLeave"
        @click="handleCardClick(index)"
      >
        <div class="card-face base" :class="{ 'is-hidden': isCardBaseHidden(index), 'is-closed': trade.state === 'closed' }">
          <div class="card-header">
            <div class="pair-block">
              <h2 :class="['pair', getPairClass(trade.pair)]" :title="formatPairLabel(trade.pair)">{{ formatPairLabel(trade.pair) }}</h2>
              <p class="trade-date-inline">{{ trade.tradeDate }}</p>
            </div>
            <div class="chips">
              <span class="direction" :class="trade.side.toLowerCase()">{{ trade.side }}</span>
              <span class="state-chip" :class="trade.state">{{ trade.stateLabel }}</span>
            </div>
          </div>

          <div class="metrics-grid">
            <div class="metric">
              <span class="caption">Entry Price</span>
              <span class="value">{{ trade.entryPrice }}</span>
            </div>
            <div class="metric">
              <span class="caption">Market Price</span>
              <span class="value">{{ trade.marketPrice }}</span>
            </div>
            <div v-if="trade.state !== 'closed'" class="metric">
              <span class="caption">Stop-loss</span>
              <span class="value stop">{{ trade.stopLoss }}</span>
            </div>
            <div v-if="trade.state !== 'closed'" class="metric">
              <span class="caption">Take Profit</span>
              <span class="value">{{ trade.takeProfit }}</span>
            </div>
          </div>

        </div>

        <div class="card-face detail" :class="{ 'is-visible': isCardDetailVisible(index) }">
          <div class="detail-header">
            <div class="detail-pair-block">
              <span :class="['detail-pair', getPairClass(trade.pair)]" :title="formatPairLabel(trade.pair)">{{ formatPairLabel(trade.pair) }}</span>
              <p class="trade-date-inline detail-trade-date-inline">{{ trade.tradeDate }}</p>
            </div>
            <div class="chips">
              <span class="detail-side" :class="trade.side.toLowerCase()">{{ trade.side }}</span>
              <span class="state-chip" :class="trade.state">{{ trade.stateLabel }}</span>
            </div>
          </div>
          <div class="detail-pnl" :class="{ negative: trade.unrealizedPnl.startsWith('-') }">
            <span class="pnl-leverage">{{ trade.leverage }}</span>
            <span class="pnl-title">Unrealized P&L (ROI)</span>
            <span class="pnl-value">{{ trade.unrealizedPnl }}</span>
            <span class="pnl-roi">{{ trade.roi }}</span>
          </div>
        </div>
      </article>
    </section>
    <div v-if="!isLoading && filteredTrades.length === 0" class="empty-state">
      Nenhuma trade encontrada para este filtro.
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { API_BASE_URL } from '@/config/env';

const TRADES_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

export default {
  name: 'Trades',
  data() {
    return {
      activeFilter: 'all',
      hoveredIndex: null,
      isLoading: false,
      errorMessage: '',
      refreshTimer: null,
      expandedMobileIndex: null,
      isMobileView: false,
      filterOptions: [
        { label: 'todas', value: 'all' },
        { label: 'ativa', value: 'active' },
        { label: 'fechada', value: 'closed' }
      ],
      trades: []
    };
  },
  computed: {
    filteredTrades() {
      if (this.activeFilter === 'all') {
        return this.trades;
      }
      return this.trades.filter(trade => trade.state === this.activeFilter);
    }
  },
  methods: {
    setFilter(filter) {
      this.activeFilter = filter;
      this.expandedMobileIndex = null;
    },
    updateViewportMode() {
      this.isMobileView = window.innerWidth <= 767;
      if (!this.isMobileView) {
        this.expandedMobileIndex = null;
      }
    },
    handleCardClick(index) {
      if (!this.isMobileView) return;
      this.expandedMobileIndex = this.expandedMobileIndex === index ? null : index;
    },
    handleCardMouseEnter(index) {
      if (this.isMobileView) return;
      this.hoveredIndex = index;
    },
    handleCardMouseLeave() {
      if (this.isMobileView) return;
      this.hoveredIndex = null;
    },
    isCardBaseHidden(index) {
      if (this.isMobileView) return this.expandedMobileIndex === index;
      return this.hoveredIndex === index;
    },
    isCardDetailVisible(index) {
      if (this.isMobileView) return this.expandedMobileIndex === index;
      return this.hoveredIndex === index;
    },
    getPairClass(pair) {
      const normalized = String(pair || "").toUpperCase();
      if (normalized.includes("BTC")) return "pair-btc";
      if (normalized.includes("ETH")) return "pair-eth";
      if (normalized.includes("SOL")) return "pair-sol";
      return "";
    },
    formatPairLabel(pair) {
      const value = String(pair || "").trim();
      if (!value) return "-";
      return value
        .replace(/[/]USDT(\.P)?$/i, "")
        .replace(/USDT(\.P)?$/i, "")
        .replace(/[/_-]+$/g, "");
    },
    formatPrice(value) {
      const numeric = Number(value);
      if (!Number.isFinite(numeric) || numeric <= 0) return '-';
      const digits = numeric >= 1000 ? 2 : numeric >= 1 ? 3 : 6;
      return `${new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: digits
      }).format(numeric)}`;
    },
    formatPnl(value) {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return '0.00 USDT';
      const sign = numeric > 0 ? '+' : '';
      return `${sign}${numeric.toFixed(2)} USDT`;
    },
    formatRoi(value) {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return '0.00%';
      const sign = numeric > 0 ? '+' : '';
      return `${sign}${numeric.toFixed(2)}%`;
    },
    normalizeTradeSide(raw) {
      const candidates = [
        raw?.position_side,
        raw?.positionSide,
        raw?.side,
        raw?.trade_side,
        raw?.tradeSide
      ];

      for (const candidate of candidates) {
        const value = String(candidate || '').trim().toUpperCase();
        if (!value) continue;
        if (value === 'LONG' || value === 'BUY') return 'LONG';
        if (value === 'SHORT' || value === 'SELL') return 'SHORT';
      }

      return 'LONG';
    },
    formatTradeDate(raw) {
      const candidates = [
        raw?.opened_at,
        raw?.openedAt,
        raw?.entry_time,
        raw?.entryTime,
        raw?.updated_time_ms,
        raw?.updatedTimeMs,
        raw?.created_at,
        raw?.createdAt,
        raw?.updated_at,
        raw?.updatedAt,
        raw?.timestamp,
        raw?.time
      ];

      let resolved = null;
      for (const candidate of candidates) {
        if (candidate === null || candidate === undefined || candidate === '') continue;

        if (typeof candidate === 'number' || /^\d+$/.test(String(candidate))) {
          const numeric = Number(candidate);
          if (Number.isFinite(numeric) && numeric > 0) {
            resolved = new Date(numeric > 1e12 ? numeric : numeric * 1000);
          }
        } else {
          const parsed = new Date(candidate);
          if (!Number.isNaN(parsed.getTime())) {
            resolved = parsed;
          }
        }

        if (resolved && !Number.isNaN(resolved.getTime())) break;
      }

      if (!resolved || Number.isNaN(resolved.getTime())) return '-';

      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(resolved);
    },
    mapTrade(raw) {
      const side = this.normalizeTradeSide(raw);
      return {
        pair: raw.pair || raw.symbol || '-',
        side,
        entryPrice: this.formatPrice(raw.entry_price),
        marketPrice: this.formatPrice(raw.market_price),
        stopLoss: this.formatPrice(raw.stop_loss),
        takeProfit: this.formatPrice(raw.take_profit),
        state: raw.state === 'closed' ? 'closed' : 'active',
        stateLabel: raw.state === 'closed' ? 'fechada' : 'ativa',
        tradeDate: this.formatTradeDate(raw),
        leverage: raw.leverage || '-',
        marginType: raw.margin_type || '-',
        unrealizedPnl: this.formatPnl(raw.pnl),
        roi: this.formatRoi(raw.roi)
      };
    },
    async fetchTrades() {
      if (typeof document !== 'undefined' && document.hidden) {
        return;
      }
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        this.errorMessage = 'Faça login para carregar trades reais.';
        this.trades = [];
        return;
      }

      this.isLoading = true;
      this.errorMessage = '';
      try {
        const response = await axios.get(`${API_BASE_URL}/api/v1/bybit/trades`, {
          params: { limit: 120 },
          headers: { Authorization: `Bearer ${token}` }
        });
        const rows = Array.isArray(response?.data?.trades) ? response.data.trades : [];
        this.trades = rows.map(this.mapTrade);
      } catch (error) {
        console.error('Erro ao carregar trades:', error);
        this.errorMessage = error?.response?.data?.error || 'Erro ao carregar trades da Bybit.';
      } finally {
        this.isLoading = false;
      }
    },
    handleVisibilityChange() {
      if (!document.hidden) {
        this.fetchTrades();
      }
    }
  },
  mounted() {
    this.updateViewportMode();
    window.addEventListener('resize', this.updateViewportMode);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.fetchTrades();
    this.refreshTimer = window.setInterval(() => this.fetchTrades(), TRADES_REFRESH_INTERVAL_MS);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateViewportMode);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    if (this.refreshTimer) {
      window.clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }
};
</script>

<style scoped>
.trades-page {
  min-height: 100vh;
  padding: calc(var(--app-header-h) + 20px) var(--app-gutter) 60px;
  color: #f3f6ff;
  font-family: 'Montserrat', sans-serif;
  background: radial-gradient(circle at top left, rgba(78, 115, 223, 0.12), transparent 45%),
    radial-gradient(circle at top right, rgba(155, 89, 182, 0.14), transparent 40%),
    #11131a;
  position: relative;
}

.trades-page::after {
  content: '';
  position: absolute;
  inset: 0;
  background-image: linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
  background-size: 32px 32px;
  opacity: 0.4;
  pointer-events: none;
}

.trades-page > * {
  position: relative;
  z-index: 1;
}

.signals-header {
  max-width: 1080px;
  margin: 0 auto 32px;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: center;
  gap: 16px;
  background: transparent;
}

.loading-banner,
.error-banner,
.empty-state {
  max-width: 1080px;
  margin: 0 auto 16px;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
}

.loading-banner {
  background: rgba(78, 115, 223, 0.15);
  color: #c9d5ff;
  border: 1px solid rgba(78, 115, 223, 0.35);
}

.error-banner {
  background: rgba(255, 107, 107, 0.12);
  color: #ffb0b0;
  border: 1px solid rgba(255, 107, 107, 0.35);
}

.empty-state {
  text-align: center;
  color: #b8c2db;
  background: rgba(17, 23, 35, 0.58);
  border: 1px solid rgba(115, 148, 240, 0.2);
}



.filter-group {
  display: inline-flex;
  gap: 12px;
  padding: 4px;
  border-radius: 999px;
  background: rgba(20, 26, 38, 0.6);
  border: 1px solid rgba(115, 148, 240, 0.25);
  backdrop-filter: blur(8px);
}

.filter-button {
  min-width: 90px;
  padding: 10px 18px;
  border-radius: 999px;
  border: none;
  background: transparent;
  color: rgba(226, 232, 255, 0.75);
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  cursor: pointer;
  transition: all 0.2s ease;
}

.filter-button:hover,
.filter-button.active {
  background: linear-gradient(120deg, rgba(78, 115, 223, 0.9), rgba(155, 89, 182, 0.9));
  color: #fff;
  box-shadow: 0 0 14px rgba(115, 148, 240, 0.4);
}

.signals-grid {
  max-width: 1080px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

.signal-card {
  position: relative;
  display: block;
  min-height: 260px;
  border-radius: 18px;
  border: 1px solid rgba(115, 148, 240, 0.25);
  background: transparent;
  overflow: hidden;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.signal-card.closed {
  min-height: 190px;
}


.signal-card.is-hovered {
  transform: translateY(-4px);
  box-shadow: 0 18px 40px rgba(12, 18, 32, 0.55);
}

.card-face {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 14px;
  padding: 22px;
  transition: opacity 0.25s ease, transform 0.25s ease;
}





.card-face.base {
  background: linear-gradient(155deg, rgba(17, 20, 32, 0.92), rgba(19, 24, 39, 0.88));
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
  justify-content: space-between;
}

.card-face.base.is-closed {
  justify-content: flex-start;
  gap: 16px;
}




.card-face.base.is-hidden {
  opacity: 0;
  transform: translateY(-8px);
  pointer-events: none;
}



.card-face.detail {
  background: linear-gradient(160deg, rgba(10, 15, 28, 0.95), rgba(18, 24, 40, 0.88));
  border: none;
  border-radius: 18px;
  opacity: 0;
  transform: translateY(8px);
  pointer-events: none;
  gap: 12px;
  padding-bottom: 20px;
}





.card-face.detail.is-visible {
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
}




.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
}

.pair-block,
.detail-pair-block {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  flex: 1 1 auto;
}

.chips {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.state-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 0.65rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 600;
  background: rgba(148, 163, 184, 0.15);
  color: rgba(226, 232, 255, 0.9);
}

.state-chip.active {
  background: rgba(37, 211, 102, 0.18);
  color: #5ce6a7;
  border: 1px solid rgba(37, 211, 102, 0.35);
}

.state-chip.closed {
  background: rgba(255, 82, 82, 0.18);
  color: #ff6b6b;
  border: 1px solid rgba(255, 82, 82, 0.35);
}





.pair {
  margin: 0;
  font-size: 1.1rem;
  letter-spacing: 0.08em;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.trade-date-inline {
  margin: 0;
  font-size: 0.7rem;
  line-height: 1.25;
  font-weight: 600;
  color: rgba(184, 194, 219, 0.76);
  letter-spacing: 0.04em;
}

.pair-btc,
.detail-pair.pair-btc {
  color: #ffd166;
}

.pair-eth,
.detail-pair.pair-eth {
  color: #4e73df;
}

.pair-sol,
.detail-pair.pair-sol {
  background: linear-gradient(110deg, #a78bfa 0%, #3498db 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  -webkit-text-fill-color: transparent;
}

.direction {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 8px;
  font-size: 0.7rem;
  letter-spacing: 0.1em;
  font-weight: 700;
  margin: 0;
}



.direction.long {
  background: rgba(37, 211, 102, 0.15);
  color: #25d366;
  border: 1px solid rgba(37, 211, 102, 0.35);
}

.direction.short {
  background: rgba(255, 82, 82, 0.12);
  color: #ff5252;
  border: 1px solid rgba(255, 82, 82, 0.35);
}







.metrics-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 18px;
  margin-top: 16px;
}




.metric {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  border-radius: 12px;
  background: rgba(17, 23, 33, 0.6);
  border: 1px solid rgba(115, 148, 240, 0.12);
}

.caption {
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: rgba(226, 232, 255, 0.55);
}

.metric .value {
  font-size: 0.95rem;
  font-weight: 600;
  color: #eef3ff;
}

.metric .value.stop {
  color: #ff6b6b;
}

/* remove change percentage from base card */

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  min-width: 0;
}




.detail-pair {
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.detail-trade-date-inline {
  color: rgba(201, 213, 255, 0.72);
}

.detail-side {
  padding: 6px 12px;
  border-radius: 10px;
  font-size: 0.75rem;
  letter-spacing: 0.12em;
  font-weight: 700;
}

.detail-side.long {
  background: rgba(37, 211, 102, 0.18);
  color: #5ce6a7;
  border: 1px solid rgba(37, 211, 102, 0.35);
}

.detail-side.short {
  background: rgba(255, 82, 82, 0.18);
  color: #ff6b6b;
  border: 1px solid rgba(255, 82, 82, 0.35);
}

.detail-meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 6px;
}



.meta-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.meta-label {
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: rgba(226, 232, 255, 0.55);
}

.meta-value {
  font-size: 0.95rem;
  font-weight: 600;
  color: #eef3ff;
}

.detail-pnl {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  border-radius: 14px;
  background: rgba(17, 23, 35, 0.7);
  border: 1px solid rgba(115, 148, 240, 0.2);
  position: relative;
}

.pnl-leverage {
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #d7e3ff;
  background: rgba(78, 115, 223, 0.18);
  border: 1px solid rgba(115, 148, 240, 0.34);
}

.pnl-title {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: rgba(226, 232, 255, 0.6);
}

.pnl-value {
  font-size: 1.05rem;
  font-weight: 700;
}

.pnl-roi {
  font-size: 0.9rem;
  font-weight: 600;
}

.detail-pnl.negative .pnl-value,
.detail-pnl.negative .pnl-roi {
  color: #ff6b6b;
}

.detail-pnl:not(.negative) .pnl-value,
.detail-pnl:not(.negative) .pnl-roi {
  color: #7de8b8;
}

@media (max-width: 767px) {
  .signals-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .trades-page {
    padding-top: calc(var(--app-header-h) + 16px);
  }

  .signal-card {
    min-height: auto;
  }

  .signal-card.closed {
    min-height: auto;
  }

  .card-face {
    position: relative;
    inset: auto;
    height: auto;
    padding: 16px;
  }

  .card-face.base.is-hidden {
    display: none;
  }

  .card-face.detail {
    display: none;
    border-top: 1px solid rgba(115, 148, 240, 0.18);
    border-radius: 0 0 18px 18px;
  }

  .card-face.detail.is-visible {
    display: flex;
    opacity: 1;
    transform: none;
    pointer-events: auto;
  }


  .signals-grid {
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  }

  .metrics-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 18px;
}




  .detail-meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 12px;
}


}

</style>


















